import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Plus, Trash2, Edit, Eye, Search, MoreVertical,
    Users, Mail, Phone, MapPin, Calendar,
    CheckCircle, XCircle, User, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react'
import { useToast } from '../hooks/useToast'
import { supabase } from '../services/supabase'
import Button from '../components/Button'
import ModalUsuario from '../components/ModalUsuario'
import ModalAdminUsuario from '../components/ModalAdminUsuario'
import { ToastContainer } from '../components/Toast'

interface Usuario {
    id: string
    user_id: string
    nome: string
    email: string
    telefone: string
    cep: string | null
    endereco: string | null
    cidade: string | null
    estado: string | null
    created_at: string
    last_sign_in_at: string | null
    email_confirmed_at: string | null
    total_vendas: number
    total_comissoes: number
    status: 'ativo' | 'inativo'
}

export default function AdminUsuarios() {
    const navigate = useNavigate()
    const { toasts, showSuccess, showError } = useToast()
    const [usuarios, setUsuarios] = useState<Usuario[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('todos')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [usuarioToDelete, setUsuarioToDelete] = useState<Usuario | null>(null)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [sortColumn, setSortColumn] = useState<string | null>(null)
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false)

    useEffect(() => {
        loadUsuarios()
    }, [])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            const dropdown = target.closest('.dropdown-container')

            if (activeDropdown && !dropdown) {
                setActiveDropdown(null)
            }
        }

        if (activeDropdown) {
            document.addEventListener('click', handleClickOutside)
        }

        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [activeDropdown])

    const loadUsuarios = async () => {
        try {
            setLoading(true)

            // Buscar todos os corretores
            const { data: corretoresData, error: corretoresError } = await supabase
                .from('corretores')
                .select('*')
                .order('created_at', { ascending: false })

            if (corretoresError) throw corretoresError

            // Buscar último login de todos os usuários via RPC
            const { data: loginsData, error: loginsError } = await supabase
                .rpc('obter_ultimo_login_usuarios')

            if (loginsError) {
                console.warn('Não foi possível buscar últimos logins:', loginsError)
            }

            // Criar mapa de user_id -> dados de autenticação para acesso rápido
            const loginsMap = new Map()
            if (loginsData) {
                loginsData.forEach((login: any) => {
                    loginsMap.set(login.user_id, {
                        last_sign_in_at: login.last_sign_in_at,
                        email_confirmed_at: login.email_confirmed_at
                    })
                })
            }

            // Para cada corretor, buscar estatísticas e último login
            const usuariosComStats = await Promise.all(
                (corretoresData || []).map(async (corretor) => {
                    // Buscar dados de autenticação do mapa
                    const authData = loginsMap.get(corretor.user_id)
                    const lastSignInAt = authData?.last_sign_in_at || null
                    const emailConfirmedAt = authData?.email_confirmed_at || null

                    // Buscar vendas do corretor
                    const { data: vendasData, error: vendasError } = await supabase
                        .from('vendas')
                        .select('id, valor_venda')
                        .eq('corretor_id', corretor.id)

                    if (vendasError) {
                        console.error('Erro ao buscar vendas do corretor:', corretor.nome, vendasError)
                    }

                    const vendasCount = vendasData?.length || 0

                    // Buscar comissões das vendas do corretor
                    let totalComissoes = 0
                    if (vendasData && vendasData.length > 0) {
                        const vendaIds = vendasData.map(venda => venda.id)

                        const { data: comissoesData, error: comissoesError } = await supabase
                            .from('comissoes')
                            .select('valor_comissao')
                            .in('venda_id', vendaIds)

                        if (comissoesError) {
                            console.error('Erro ao buscar comissões do corretor:', corretor.nome, comissoesError)
                        } else {
                            totalComissoes = comissoesData?.reduce((acc, comissao) => acc + comissao.valor_comissao, 0) || 0
                        }
                    }

                    return {
                        ...corretor,
                        last_sign_in_at: lastSignInAt,
                        email_confirmed_at: emailConfirmedAt,
                        total_vendas: vendasCount,
                        total_comissoes: totalComissoes,
                        status: 'ativo' as const // Por padrão, todos são ativos
                    }
                })
            )

            setUsuarios(usuariosComStats)
        } catch (err) {
            console.error('Erro ao carregar usuários:', err)
            showError('Erro ao carregar lista de usuários. Verifique sua conexão e tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!usuarioToDelete) return

        try {
            const { error } = await supabase
                .from('corretores')
                .delete()
                .eq('id', usuarioToDelete.id)

            if (error) throw error

            showSuccess(`Usuário "${usuarioToDelete.nome}" excluído com sucesso!`)
            loadUsuarios()
        } catch (err) {
            console.error('Erro ao excluir usuário:', err)
            showError(`Erro ao excluir usuário "${usuarioToDelete.nome}". Tente novamente.`)
        } finally {
            setIsDeleteModalOpen(false)
            setUsuarioToDelete(null)
        }
    }

    const openDeleteModal = (usuario: Usuario) => {
        setUsuarioToDelete(usuario)
        setIsDeleteModalOpen(true)
        setActiveDropdown(null)
    }

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false)
        setUsuarioToDelete(null)
    }

    const handleEditUsuario = (usuario: Usuario) => {
        setEditingUsuario(usuario)
        setIsModalOpen(true)
        setActiveDropdown(null)
    }

    const toggleDropdown = (usuarioId: string, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation()
        }
        setActiveDropdown(activeDropdown === usuarioId ? null : usuarioId)
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ativo':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'inativo':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    const formatLastLogin = (lastSignIn: string | null, emailConfirmed: string | null): string => {
        // Se o email não foi confirmado, mostrar "Usuário não ativado"
        if (!emailConfirmed) {
            return 'Usuário não ativado'
        }

        // Se não tem último login mas o email foi confirmado
        if (!lastSignIn) return '-'

        const date = new Date(lastSignIn)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        // Se foi há menos de 1 hora
        if (diffMins < 60) {
            return diffMins <= 1 ? 'Agora' : `Há ${diffMins} min`
        }

        // Se foi há menos de 24 horas
        if (diffHours < 24) {
            return `Há ${diffHours}h`
        }

        // Se foi há menos de 7 dias
        if (diffDays < 7) {
            return `Há ${diffDays}d`
        }

        // Caso contrário, mostrar data completa
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }



    const filteredUsuarios = usuarios.filter(usuario => {
        const matchesSearch = usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (usuario.telefone && usuario.telefone.includes(searchTerm))

        const matchesStatus = statusFilter === 'todos' || usuario.status === statusFilter

        return matchesSearch && matchesStatus
    })

    // Função para ordenar os usuários
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            // Se já está ordenando por esta coluna, inverte a direção
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            // Nova coluna, ordena ascendente
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    // Aplicar ordenação
    const sortedUsuarios = [...filteredUsuarios].sort((a, b) => {
        if (!sortColumn) return 0

        let aValue: any
        let bValue: any

        switch (sortColumn) {
            case 'nome':
                aValue = a.nome.toLowerCase()
                bValue = b.nome.toLowerCase()
                break
            case 'email':
                aValue = a.email.toLowerCase()
                bValue = b.email.toLowerCase()
                break
            case 'telefone':
                aValue = a.telefone || ''
                bValue = b.telefone || ''
                break
            case 'cidade':
                aValue = (a.cidade || '').toLowerCase()
                bValue = (b.cidade || '').toLowerCase()
                break
            case 'vendas':
                aValue = a.total_vendas
                bValue = b.total_vendas
                break
            case 'comissoes':
                aValue = a.total_comissoes
                bValue = b.total_comissoes
                break
            case 'status':
                aValue = a.status
                bValue = b.status
                break
            case 'cadastro':
                aValue = new Date(a.created_at).getTime()
                bValue = new Date(b.created_at).getTime()
                break
            case 'ultimo_login':
                aValue = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0
                bValue = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0
                break
            default:
                return 0
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
    })

    // Função para renderizar o ícone de ordenação
    const getSortIcon = (column: string) => {
        if (sortColumn !== column) {
            return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 dark:text-gray-500" />
        }
        return sortDirection === 'asc'
            ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600 dark:text-blue-400" />
            : <ArrowDown className="h-3 w-3 ml-1 text-blue-600 dark:text-blue-400" />
    }

    if (loading) {
        return (
            <div className="w-full">
                <div className="w-full">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="w-full">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Gerenciar Usuários
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Gerencie corretores e suas informações
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={() => {
                                    setEditingUsuario(null)
                                    setIsModalOpen(true)
                                }}
                                variant="btn_add"
                                className="flex items-center gap-2"
                            >
                                <Plus size={20} />
                                Novo Usuário
                            </Button>
                            <Button
                                onClick={() => {
                                    setIsAdminModalOpen(true)
                                }}
                                variant="btn_secondary"
                                className="flex items-center gap-2 border-2 border-purple-600 dark:border-purple-500 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                            >
                                <User size={20} />
                                Novo Administrador
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Busca */}
                        <div className="md:col-span-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuários por nome, email ou telefone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                />
                            </div>
                        </div>

                        {/* Filtro Status */}
                        <div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos os Status</option>
                                <option value="ativo">Ativo</option>
                                <option value="inativo">Inativo</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Tabela de Usuários */}
                {filteredUsuarios.length === 0 ? (
                    <div className="text-center py-16">
                        <Users className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum usuário encontrado
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {searchTerm || statusFilter !== 'todos'
                                ? 'Tente ajustar os filtros de busca.'
                                : 'Cadastre seu primeiro usuário para começar.'
                            }
                        </p>
                        {!searchTerm && statusFilter === 'todos' && (
                            <Button
                                onClick={() => {
                                    setEditingUsuario(null)
                                    setIsModalOpen(true)
                                }}
                                variant="btn_add"
                                className="px-8 py-3"
                            >
                                Cadastrar Primeiro Usuário
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('nome')}
                                        >
                                            <div className="flex items-center">
                                                Usuário
                                                {getSortIcon('nome')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('email')}
                                        >
                                            <div className="flex items-center">
                                                Contato
                                                {getSortIcon('email')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('cidade')}
                                        >
                                            <div className="flex items-center">
                                                Localização
                                                {getSortIcon('cidade')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('vendas')}
                                        >
                                            <div className="flex items-center">
                                                Vendas
                                                {getSortIcon('vendas')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('comissoes')}
                                        >
                                            <div className="flex items-center">
                                                Comissões
                                                {getSortIcon('comissoes')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center">
                                                Status
                                                {getSortIcon('status')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('cadastro')}
                                        >
                                            <div className="flex items-center">
                                                Cadastro
                                                {getSortIcon('cadastro')}
                                            </div>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                                            onClick={() => handleSort('ultimo_login')}
                                        >
                                            <div className="flex items-center">
                                                Último Login
                                                {getSortIcon('ultimo_login')}
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {sortedUsuarios.map((usuario) => (
                                        <tr key={usuario.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <User className="h-5 w-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {usuario.nome}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            ID: {usuario.id.substring(0, 8)}...
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                                                        <Mail className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                                        <span className="truncate max-w-[200px]">{usuario.email}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                        <Phone className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                                        <span>{usuario.telefone}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {(usuario.cidade || usuario.estado) ? (
                                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                        <MapPin className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                                        <span>
                                                            {usuario.cidade}
                                                            {usuario.cidade && usuario.estado && ', '}
                                                            {usuario.estado}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {usuario.total_vendas}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-green-600 dark:text-green-400">
                                                    {formatCurrency(usuario.total_comissoes)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(usuario.status)}`}>
                                                    {usuario.status === 'ativo' ? (
                                                        <>
                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                            Ativo
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle className="w-3 h-3 mr-1" />
                                                            Inativo
                                                        </>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                    <Calendar className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                                    <span>{formatDate(usuario.created_at)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Calendar className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                                    <span className={!usuario.email_confirmed_at ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-600 dark:text-gray-400'}>
                                                        {formatLastLogin(usuario.last_sign_in_at, usuario.email_confirmed_at)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="relative dropdown-container flex justify-end">
                                                    <button
                                                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                        onClick={(e: React.MouseEvent) => toggleDropdown(usuario.id, e)}
                                                    >
                                                        <MoreVertical className="h-4 w-4" />
                                                    </button>

                                                    {/* Dropdown Menu */}
                                                    {activeDropdown === usuario.id && (
                                                        <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleEditUsuario(usuario)
                                                                    }}
                                                                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Edit className="h-4 w-4" />
                                                                    Editar Usuário
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        navigate(`/admin/usuarios/${usuario.id}/vendas`)
                                                                    }}
                                                                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                    Ver Vendas
                                                                </button>
                                                                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        openDeleteModal(usuario)
                                                                    }}
                                                                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                    Excluir
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Modal de Exclusão */}
                {isDeleteModalOpen && usuarioToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                        Excluir Usuário
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        Tem certeza que deseja excluir o usuário <strong>"{usuarioToDelete.nome}"</strong>?
                                        Esta ação não pode ser desfeita e excluirá todas as vendas e comissões associadas.
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <Button
                                            variant="btn_secondary"
                                            onClick={closeDeleteModal}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="btn_secondary"
                                            onClick={handleDelete}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Usuário */}
                <ModalUsuario
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        setEditingUsuario(null)
                    }}
                    onSuccess={() => {
                        loadUsuarios()
                        setIsModalOpen(false)
                        setEditingUsuario(null)
                    }}
                    usuario={editingUsuario}
                    onShowToast={(type, title, message) => {
                        if (type === 'success') {
                            showSuccess(title, message)
                        } else if (type === 'error') {
                            showError(title, message)
                        }
                    }}
                />

                {/* Modal de Administrador */}
                <ModalAdminUsuario
                    isOpen={isAdminModalOpen}
                    onClose={() => {
                        setIsAdminModalOpen(false)
                    }}
                    onSuccess={() => {
                        setIsAdminModalOpen(false)
                        showSuccess('Operação realizada com sucesso!')
                    }}
                    adminUser={null}
                    onShowToast={(type, title, message) => {
                        if (type === 'success') {
                            showSuccess(title, message)
                        } else if (type === 'error') {
                            showError(title, message)
                        }
                    }}
                />

                {/* Toast Container */}
                <ToastContainer toasts={toasts} />
            </div>
        </div>
    )
}
